/**
 * @fileoverview Owner dashboard Expenses & Income box — inline add/edit form,
 * server-side searchable list (10/page), and CSV export. Income entries are
 * stored as negative amountCents. Lives directly above the Exports card;
 * replaced the standalone /owner/expenses screen.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { api, getToken } from '../api/client';
import Card from './Card';
import Button from './Button';
import Picker from './Picker';
import DateTimePicker from './DateTimePicker';
import InlineConfirm from './InlineConfirm';
import { useToast } from './Toast';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { formatDate } from '../utils/format';
import { downloadFile } from '../utils/fileExport';

interface ExpenseRow { id: string; category: string; description: string | null; amountCents: number; incurredOn: string; locationId: string | null }

const CATEGORIES = ['chemicals', 'labor', 'utilities', 'rent', 'maintenance', 'supplies', 'insurance', 'cash', 'income', 'other'];
const PERIODS = [
  { value: 'current_month', label: 'This month' },
  { value: 'previous_month', label: 'Last month' },
  { value: 'ytd', label: 'Year to date' },
];
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function ExpensesBox() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [expanded, setExpanded] = useState(false);
  const [period, setPeriod] = useState('current_month');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Inline add/edit form. Income entries are stored as negative cents.
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [entryType, setEntryType] = useState<'expense' | 'income'>('expense');
  const [category, setCategory] = useState('chemicals');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [incurredOn, setIncurredOn] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPage = async (p: number, q: string, per: string) => {
    try {
      const params = new URLSearchParams({ period: per, page: String(p) });
      if (q.trim()) params.set('q', q.trim());
      const d = await api<{ rows: ExpenseRow[]; totalCents: number; totalRows: number; pageCount: number; period: { label: string } }>(
        `/api/owner/expenses?${params}`);
      setRows(d.rows); setTotal(d.totalCents); setTotalRows(d.totalRows);
      setPageCount(d.pageCount ?? 1); setPeriodLabel(d.period.label);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!expanded) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPage(page, search, period), search ? 300 : 0);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [expanded, page, search, period]);

  const resetForm = () => { setEditId(null); setEntryType('expense'); setCategory('chemicals'); setAmount(''); setDescription(''); setIncurredOn(todayStr()); };

  const startEdit = (r: ExpenseRow) => {
    setEditId(r.id); setEntryType(r.amountCents < 0 ? 'income' : 'expense');
    setCategory(r.category); setAmount((Math.abs(r.amountCents) / 100).toFixed(2));
    setDescription(r.description ?? ''); setIncurredOn(r.incurredOn); setShowForm(true);
  };

  const save = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents <= 0) { toast.show({ type: 'error', title: 'Enter a valid amount' }); return; }
    setSaving(true);
    try {
      const body = { category, amountCents: entryType === 'income' ? -cents : cents, description: description.trim() || undefined, incurredOn };
      if (editId) await api(`/api/owner/expenses/${editId}`, { method: 'PUT', body });
      else await api('/api/owner/expenses', { method: 'POST', body });
      toast.show({ type: 'success', title: editId ? 'Updated' : 'Added' });
      resetForm(); setShowForm(false);
      fetchPage(page, search, period);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    setConfirmDelete(null);
    try {
      await api(`/api/owner/expenses/${id}`, { method: 'DELETE' });
      if (editId === id) { resetForm(); setShowForm(false); }
      fetchPage(page, search, period);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ period, format: 'csv' });
      if (search.trim()) params.set('q', search.trim());
      const apiBase = (process.env.EXPO_PUBLIC_API_URL as string | undefined) || '';
      const headers: Record<string, string> = {};
      if (Platform.OS !== 'web') {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      await downloadFile(`${apiBase}/api/owner/expenses?${params}`, `rinsepay-expenses-${period}.csv`,
        Object.keys(headers).length > 0 ? headers : undefined);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Export failed', message: err?.message ?? 'Unknown error' });
    }
  };

  return (
    <Card style={styles.section}>
      <TouchableOpacity onPress={() => setExpanded(!expanded)} style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Expenses & Income</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={20} color={colors.textSecondary} />
      </TouchableOpacity>
      {expanded && (
        <View style={{ gap: spacing.sm, marginTop: spacing.sm }}>
          <Text style={styles.hint}>Track costs (chemicals, labor, utilities…) and outside income (coin-box cash, vending…) so your P&L shows real profit.</Text>
          <Picker label="Period" value={period}
            onValueChange={(v) => { setPeriod(v); setPage(1); }}
            options={PERIODS.map(p => ({ label: p.label, value: p.value }))} />

          {!showForm && (
            <Button title="Add Expense or Income" variant="outline" onPress={() => { resetForm(); setShowForm(true); }}
              icon={<Ionicons name="add-circle-outline" size={16} color={colors.primary} />} />
          )}
          {showForm && (
            <View style={styles.form}>
              <Text style={styles.formTitle}>{editId ? 'Edit entry' : 'Add entry'}</Text>
              <View style={styles.typeRow}>
                {(['expense', 'income'] as const).map(t => (
                  <TouchableOpacity key={t} onPress={() => setEntryType(t)}
                    style={[styles.typeBtn, entryType === t && styles.typeBtnActive]}>
                    <Text style={[styles.typeBtnText, entryType === t && styles.typeBtnTextActive]}>
                      {t === 'expense' ? 'Expense' : 'Income'}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Picker label="Category" value={category} onValueChange={setCategory}
                options={CATEGORIES.map(c => ({ label: c.charAt(0).toUpperCase() + c.slice(1), value: c }))} />
              <Text style={styles.fieldLabel}>Amount ($)</Text>
              <TextInput style={styles.input} value={amount} onChangeText={setAmount} keyboardType="decimal-pad"
                placeholder="0.00" placeholderTextColor={colors.textLight} />
              <View style={{ marginTop: spacing.sm }}>
                <DateTimePicker label="Date" mode="date" value={incurredOn} onChange={setIncurredOn} />
              </View>
              <Text style={styles.fieldLabel}>Description (optional)</Text>
              <TextInput style={styles.input} value={description} onChangeText={setDescription} maxLength={280}
                placeholder="e.g. Soap restock" placeholderTextColor={colors.textLight} />
              <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md }}>
                <Button title={saving ? 'Saving…' : editId ? 'Update' : 'Add'} onPress={save} disabled={saving} style={{ flex: 1 }} />
                <Button title="Cancel" variant="outline" onPress={() => { resetForm(); setShowForm(false); }} />
              </View>
            </View>
          )}

          {/* Search stays mounted even with no results. */}
          <View style={styles.searchRow}>
            <Ionicons name="search" size={16} color={colors.textLight} />
            <TextInput
              style={styles.searchInput}
              value={search}
              onChangeText={(t) => { setSearch(t); setPage(1); }}
              placeholder="Search category, description, date, amount…"
              placeholderTextColor={colors.textLight}
            />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => { setSearch(''); setPage(1); }} hitSlop={8}>
                <Ionicons name="close-circle" size={16} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </View>

          {loading ? <Text style={styles.empty}>Loading…</Text>
            : rows.length === 0 ? <Text style={styles.empty}>{search ? 'No expenses match your search.' : 'No expenses this period.'}</Text>
            : rows.map(r => (
              <View key={r.id} style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.rowCat}>{r.category.charAt(0).toUpperCase() + r.category.slice(1)}{r.amountCents < 0 ? ' · income' : ''}</Text>
                  <Text style={styles.rowSub}>{formatDate(r.incurredOn)}{r.description ? ` · ${r.description}` : ''}</Text>
                </View>
                <Text style={[styles.rowAmount, r.amountCents < 0 && { color: colors.success }]}>
                  {r.amountCents < 0 ? `+${fmtMoney(-r.amountCents)}` : fmtMoney(r.amountCents)}
                </Text>
                <TouchableOpacity onPress={() => startEdit(r)} hitSlop={8} style={{ paddingHorizontal: 6 }}>
                  <Ionicons name="pencil" size={16} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setConfirmDelete(r.id)} hitSlop={8} style={{ paddingHorizontal: 6 }}>
                  <Ionicons name="trash" size={16} color={colors.danger} />
                </TouchableOpacity>
              </View>
            ))}
          <InlineConfirm visible={confirmDelete !== null} message="Delete this expense?" confirmLabel="Delete"
            onConfirm={() => confirmDelete && remove(confirmDelete)} onCancel={() => setConfirmDelete(null)} />

          {pageCount > 1 && (
            <View style={styles.pageRow}>
              <TouchableOpacity onPress={() => setPage(page - 1)} disabled={page <= 1}
                style={[styles.pageBtn, page <= 1 && { opacity: 0.4 }]}>
                <Text style={styles.pageBtnText}>Prev</Text>
              </TouchableOpacity>
              <Text style={styles.pageInfo}>Page {page} of {pageCount}</Text>
              <TouchableOpacity onPress={() => setPage(page + 1)} disabled={page >= pageCount}
                style={[styles.pageBtn, page >= pageCount && { opacity: 0.4 }]}>
                <Text style={styles.pageBtnText}>Next</Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{search ? `${totalRows} matching · ` : ''}{periodLabel || 'Period'} · net expenses</Text>
            <Text style={[styles.totalValue, total < 0 && { color: colors.success }]}>
              {total < 0 ? `+${fmtMoney(-total)}` : fmtMoney(total)}
            </Text>
          </View>
          <Button title="Expenses CSV" variant="outline" onPress={exportCsv}
            icon={<Ionicons name="download-outline" size={16} color={colors.primary} />} />
        </View>
      )}
    </Card>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  section: { marginBottom: spacing.md },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text },
  hint: { fontSize: fontSize.xs, color: c.textLight },
  form: { borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md, padding: spacing.sm },
  typeRow: { flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.xs },
  typeBtn: { flex: 1, alignItems: 'center', paddingVertical: spacing.xs, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md },
  typeBtnActive: { borderColor: c.primary, backgroundColor: c.primary + '15' },
  typeBtnText: { fontSize: fontSize.sm, color: c.textSecondary },
  typeBtnTextActive: { color: c.primary, fontWeight: fontWeight.semibold },
  formTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  fieldLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: 4, marginTop: spacing.sm },
  input: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, padding: spacing.sm, fontSize: fontSize.sm, color: c.text },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: Platform.OS === 'web' ? spacing.xs : 2 },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, paddingVertical: spacing.xs },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.xs },
  rowCat: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  rowSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  rowAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginRight: spacing.xs },
  empty: { fontSize: fontSize.sm, color: c.textLight, paddingVertical: spacing.md, textAlign: 'center' },
  pageRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.md },
  pageBtn: { paddingVertical: spacing.xs, paddingHorizontal: spacing.sm },
  pageBtnText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageInfo: { fontSize: fontSize.xs, color: c.textSecondary },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  totalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  totalValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
